import React, { useState, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { WorkItem } from "./ResponsibilityAreaTable";

type NewTaskTreeProps = {
  newTask: WorkItem;
  checkedSubIds: string[];
  setCheckedSubIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export default function NewTaskTree({ newTask, checkedSubIds, setCheckedSubIds }: NewTaskTreeProps) {
  const [tree, setTree] = useState(null);

  useEffect(() => {
    if (!newTask?.ToCs) return;

    const attachParents = (node, parent = null) => {
      node.__parent = parent;

      if (!node.Id) node.Id = node.DocumentId;
      node.IsAssignable = false;
      if (newTask.MissingSubIds?.some((subId) => subId.SubId === node.Id)) {
        node.IsAssignable = true;
        if (node.Children && node.Children.length === 0) {
          setCheckedSubIds((prev) => [...new Set([...prev, node.Id])]);
        }
        makeAllParentsAssignable(node);
      }

      node.Children?.forEach((child) => attachParents(child, node));
      return node;
    };

    const clonedTree = structuredClone(newTask.ToCs);
    const processedTree = attachParents(clonedTree);

    setTree(processedTree);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newTask]);

  const handleCheck = (node, isChecked) => {
    setCheckedSubIds((prev: string[]) => {
      let updated = new Set(prev); // ensure uniqueness

      // Set descendants (only leaf nodes)
      const setDescendants = (n, state) => {
        if (!n.Children || n.Children.length === 0) {
          const id = n.Id || n.DocumentId;
          if (state) {
            if (n.IsAssignable) updated.add(id);
          } else {
            updated.delete(id);
          }
        }
        n.Children?.forEach((child) => setDescendants(child, state));
      };

      setDescendants(node, isChecked);
      return [...updated]; // convert Set back to array
    });
  };

  return (
    <div className="p-4">
      {tree && <TreeNode node={tree} checkedSubIds={checkedSubIds} onCheck={handleCheck} defaultExpanded />}
    </div>
  );
}

function TreeNode({ node, checkedSubIds, onCheck, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!node) return null;
  if (!node.IsAssignable) return null;

  const toggleExpand = () => setExpanded((prev) => !prev);

  const isParent = node.Children && node.Children.length > 0;
  const allChildrenChecked =
    isParent &&
    node.Children.every((child) =>
      !child.Children || child.Children.length === 0
        ? checkedSubIds.includes(child.Id || child.DocumentId)
        : areAllDescendantsChecked(child, checkedSubIds)
    );
  const someChildrenChecked =
    isParent &&
    node.Children.some((child) =>
      !child.Children || child.Children.length === 0
        ? checkedSubIds.includes(child.Id || child.DocumentId)
        : areSomeDescendantsChecked(child, checkedSubIds)
    );

  return (
    <div className="ml-4" key={node.Id || node.DocumentId}>
      <div className="flex items-center">
        {isParent && (
          <button onClick={toggleExpand} className="w-4 mr-1 text-gray-500 focus:outline-none">
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </button>
        )}
        <div className="relative">
          <input
            type="checkbox"
            checked={isParent ? allChildrenChecked : !!checkedSubIds.includes(node.Id || node.DocumentId)}
            ref={(el) => {
              if (el) el.indeterminate = isParent && !allChildrenChecked && someChildrenChecked;
            }}
            onChange={(e) => onCheck(node, e.target.checked)}
            className="mr-2 w-4 h-4 border rounded"
          />
        </div>
        <span>{node.Title}</span>
      </div>

      {expanded && isParent && (
        <div className="ml-6 mt-1 border-l border-gray-300 pl-2">
          {node.Children.map((child) => (
            <TreeNode
              key={child.Id || child.DocumentId}
              node={child}
              checkedSubIds={checkedSubIds}
              onCheck={onCheck}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Helper functions to check descendants’ state
function areAllDescendantsChecked(node, checkedSubIds) {
  if (!node.Children || node.Children.length === 0) {
    return !!checkedSubIds.includes(node.Id || node.DocumentId);
  }
  return node.Children.every((child) => areAllDescendantsChecked(child, checkedSubIds));
}

function areSomeDescendantsChecked(node, checkedSubIds) {
  if (!node.Children || node.Children.length === 0) {
    return !!checkedSubIds.includes(node.Id || node.DocumentId);
  }
  return node.Children.some((child) => areSomeDescendantsChecked(child, checkedSubIds));
}

function makeAllParentsAssignable(node) {
  if (node.__parent && !node.__parent.IsAssignable) {
    node.__parent.IsAssignable = true;
    makeAllParentsAssignable(node.__parent);
  }
}
